<template>
    <v-row class="ma-0 pb-offset">
        <!-- Impostazioni Notifiche Generali -->
        <v-col cols="12" class="pa-0 mb-4">
            <v-card class="round-card background-2">
                <v-card-title class="pa-3">
                    <v-icon color="primary" class="mr-3">mdi-cog</v-icon>
                    <p>Impostazioni Generali</p>
                </v-card-title>
                <v-divider></v-divider>

                <v-list two-line subheader class="background-2 pa-2">
                    <v-list-item>
                        <v-list-item-action>
                            <v-checkbox
                                color="primary"
                                hide-details
                                v-model="notificationSettings['sla_enabled']"
                            ></v-checkbox>
                        </v-list-item-action>
                        <v-list-item-content>
                            <v-list-item-title>Gestione SLA</v-list-item-title>
                            <v-list-item-subtitle>Attiva il tracciamento automatico dei tempi SLA (risposta e risoluzione) sui ticket</v-list-item-subtitle>
                        </v-list-item-content>
                    </v-list-item>

                    <v-list-item>
                        <v-list-item-action>
                            <v-checkbox
                                color="primary"
                                hide-details
                                v-model="notificationSettings['notify_carnet_hours_low']"
                            ></v-checkbox>
                        </v-list-item-action>
                        <v-list-item-content>
                            <v-list-item-title>Ore Carnet in esaurimento</v-list-item-title>
                            <v-list-item-subtitle>Invia una notifica quando le ore residue di un contratto carnet scendono sotto una soglia percentuale</v-list-item-subtitle>
                        </v-list-item-content>
                    </v-list-item>
                </v-list>

                <v-slide-y-transition>
                    <v-list two-line subheader class="background-2 px-2 pb-2 pt-0" v-if="notificationSettings['notify_carnet_hours_low']">
                        <v-list-item>
                            <v-list-item-action>
                            </v-list-item-action>
                            <v-list-item-content style="height: 90px; padding-top: 24px;">
                                <v-text-field
                                    label="Soglia percentuale (%)"
                                    v-model="notificationSettings['carnet_hours_low_threshold']"
                                    type="number"
                                    outlined
                                    dense
                                    required
                                    hint="Default: 20%"
                                    persistent-hint
                                ></v-text-field>
                            </v-list-item-content>
                        </v-list-item>
                    </v-list>
                </v-slide-y-transition>

                <v-list two-line subheader class="background-2 px-2 pb-2 pt-0">
                    <v-list-item>
                        <v-list-item-action>
                            <v-checkbox
                                color="primary"
                                hide-details
                                v-model="notificationSettings['block_interventions_on_triage']"
                            ></v-checkbox>
                        </v-list-item-action>
                        <v-list-item-content>
                            <v-list-item-title>Blocca interventi su ticket in triage</v-list-item-title>
                            <v-list-item-subtitle>Impedisce l'inserimento di nuovi interventi finché il ticket non esce dalla fase di triage</v-list-item-subtitle>
                        </v-list-item-content>
                    </v-list-item>
                </v-list>

                <v-divider></v-divider>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn text class="pa-2 mx-2" color="primary" :loading="savingNotifications" @click="saveNotificationSettings">Salva</v-btn>
                </v-card-actions>
            </v-card>
        </v-col>

        <!-- Header con titolo e pulsante aggiungi -->
        <v-col cols="12" class="pa-0 mb-4">
            <v-card class="round-card background-2">
                <v-card-title class="pa-3">
                    <v-icon color="primary" class="mr-3">mdi-email-sync</v-icon>
                    <p>Account Email Helpdesk</p>
                    <v-spacer></v-spacer>
                    <v-btn
                        color="primary"
                        @click="showAddAccountDialog = true"
                        small
                    >
                        <v-icon left>mdi-plus</v-icon>
                        Aggiungi Account
                    </v-btn>
                </v-card-title>
            </v-card>
        </v-col>

        <!-- Loading -->
        <v-col v-if="loading" cols="12" class="pa-0 text-center">
            <v-progress-circular
                indeterminate
                color="primary"
                size="48"
            ></v-progress-circular>
        </v-col>

        <!-- Lista Account Helpdesk -->
        <template v-else>
            <!-- Selector Account di Default (visibile solo se > 1 account) -->
            <v-col v-if="helpdeskAccounts.length > 1" cols="12" class="pa-0 mb-4">
                <v-card class="round-card background-2">
                    <v-card-text class="pa-4">
                        <div class="d-flex align-center">
                            <v-icon color="primary" class="mr-3">mdi-star</v-icon>
                            <div class="flex-grow-1">
                                <v-select
                                    v-model="defaultAccountId"
                                    :items="helpdeskAccounts"
                                    item-text="name"
                                    item-value="id"
                                    label="Account di Default"
                                    hint="Usato per ticket creati manualmente quando non specificato"
                                    persistent-hint
                                    outlined
                                    dense
                                    :menu-props="{'offset-y': true}"
                                    hide-details="auto"
                                    class="mb-0"
                                    @change="saveDefaultAccount"
                                    :loading="savingDefaultAccount"
                                ></v-select>
                            </div>
                        </div>
                    </v-card-text>
                </v-card>
            </v-col>

            <!-- Messaggio se non ci sono account -->
            <v-col v-if="!helpdeskAccounts.length" cols="12" class="pa-0">
                <v-alert type="info" text class="mb-4">
                    <div class="font-weight-medium">Nessun Account Helpdesk Configurato</div>
                    <div class="caption mt-1">
                        Collega almeno una casella di posta (Google o Microsoft 365) per ricevere email nell'Helpdesk.
                        Clicca su "Aggiungi Account" per iniziare.
                    </div>
                </v-alert>
            </v-col>

            <!-- Card per ogni account -->
            <v-col
                v-for="account in helpdeskAccounts"
                :key="account.id"
                cols="12"
                class="pa-0"
            >
                <helpdesk-account-card
                    :account="account"
                    :options="formOptions"
                    @remove="removeAccount"
                    @reconnect="reconnectAccount"
                    @saved="loadSettings"
                ></helpdesk-account-card>
            </v-col>
        </template>

        <!-- Dialog per aggiungere account -->
        <v-dialog v-model="showAddAccountDialog" max-width="500">
            <v-card class="round-card">
                <v-card-title>
                    <v-icon color="primary" class="mr-2">mdi-email-plus</v-icon>
                    Aggiungi Account Helpdesk
                </v-card-title>
                <v-divider></v-divider>

                <v-card-text class="pt-4">
                    <template v-if="availableAccounts.length">
                        <div class="caption grey--text mb-3">
                            Seleziona un account già collegato da configurare come casella Helpdesk:
                        </div>
                        <v-select
                            v-model="selectedAccountId"
                            :items="availableAccounts"
                            item-text="name"
                            item-value="id"
                            label="Account di posta"
                            outlined
                            dense
                            :menu-props="{'offset-y': true}"
                        >
                            <template v-slot:item="{ item }">
                                <v-list-item-avatar size="32">
                                    <v-img v-if="item.avatar" :src="item.avatar"></v-img>
                                    <v-icon v-else>{{ iconaProvider(item.provider) }}</v-icon>
                                </v-list-item-avatar>
                                <v-list-item-content>
                                    <v-list-item-title>{{ item.name }}</v-list-item-title>
                                    <v-list-item-subtitle>{{ item.display_name }}</v-list-item-subtitle>
                                </v-list-item-content>
                            </template>
                        </v-select>
                    </template>

                    <v-divider v-if="availableAccounts.length" class="my-4"></v-divider>

                    <div class="caption grey--text mb-3">
                        {{ availableAccounts.length ? 'Oppure collega' : 'Collega' }} una nuova casella:
                    </div>
                    <v-btn
                        outlined
                        color="primary"
                        class="mb-2"
                        @click="connectGoogleAccount()"
                        block
                    >
                        <v-icon left>mdi-google</v-icon>
                        Collega account Google
                    </v-btn>
                    <v-btn
                        v-if="providersDisponibili.includes('microsoft')"
                        outlined
                        color="primary"
                        @click="connectMicrosoftAccount()"
                        block
                    >
                        <v-icon left>mdi-microsoft</v-icon>
                        Collega casella Microsoft 365
                    </v-btn>
                </v-card-text>

                <v-divider></v-divider>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn text @click="showAddAccountDialog = false">Annulla</v-btn>
                    <v-btn
                        color="primary"
                        @click="addHelpdeskAccount"
                        :loading="addingAccount"
                        :disabled="!selectedAccountId"
                    >
                        Aggiungi
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- Dialog conferma rimozione -->
        <v-dialog v-model="showRemoveDialog" max-width="400">
            <v-card class="round-card">
                <v-card-title>
                    <v-icon color="error" class="mr-2">mdi-alert</v-icon>
                    Conferma Rimozione
                </v-card-title>
                <v-divider></v-divider>
                <v-card-text class="pt-4">
                    Sei sicuro di voler rimuovere questo account dalla configurazione Helpdesk?
                    <br><br>
                    <strong>Nota:</strong> L'account non verrà eliminato, ma non riceverà più email per l'Helpdesk.
                </v-card-text>
                <v-divider></v-divider>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn text @click="showRemoveDialog = false">Annulla</v-btn>
                    <v-btn
                        color="error"
                        @click="confirmRemoveAccount"
                        :loading="removingAccount"
                    >
                        Rimuovi
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </v-row>
</template>

<script>
import HelpdeskAccountCard from './HelpdeskAccountCard.vue'

export default {
    name: 'HelpdeskOptions',

    components: {
        HelpdeskAccountCard
    },

    data() {
        return {
            loading: true,
            helpdeskAccounts: [],
            troubleTemplates: [],
            interventionTemplates: [],
            availableAccounts: [],
            selectedAccountId: null,
            showAddAccountDialog: false,
            addingAccount: false,
            showRemoveDialog: false,
            accountToRemove: null,
            removingAccount: false,
            vats: [],
            paymentTerms: [],
            layoutgroups: [],
            products: [],
            companies: [],
            users: [],
            priorities: [],
            requestTypes: [],
            sectionals: [],
            defaultAccountId: null,
            providersDisponibili: [],
            savingDefaultAccount: false,
            notificationSettings: {
                'sla_enabled': false,
                'notify_carnet_hours_low': false,
                'carnet_hours_low_threshold': null,
                'block_interventions_on_triage': false
            },
            savingNotifications: false
        }
    },

    computed: {
        formOptions() {
            return {
                troubleTemplates: this.troubleTemplates,
                interventionTemplates: this.interventionTemplates,
                vats: this.vats,
                paymentTerms: this.paymentTerms,
                layoutgroups: this.layoutgroups,
                sectionals: this.sectionals,
                products: this.products,
                companies: this.companies,
                users: this.users,
                priorities: this.priorities,
                requestTypes: this.requestTypes
            }
        }
    },

    methods: {
        loadSettings() {
            this.loading = true

            this.axios.get('get-helpdesk-settings')
                .then(response => {
                    const data = response.data.data || {}

                    this.helpdeskAccounts = data.helpdesk_accounts || []
                    this.troubleTemplates = data.trouble_templates || []
                    this.interventionTemplates = data.intervention_templates || []
                    this.defaultAccountId = data.default_account_id || null
                    this.vats = data.vats || []
                    this.paymentTerms = data.payment_terms || []
                    this.layoutgroups = data.layoutgroups || []
                    this.sectionals = data.sectionals || []
                    this.products = data.products || []
                    this.companies = data.companies || []
                    this.users = data.users || []
                    this.priorities = data.priorities || []
                    this.requestTypes = data.request_types || []
                    this.providersDisponibili = data.providers_disponibili || ['google']

                    // Filtra account disponibili (non già usati come helpdesk)
                    const helpdeskIds = this.helpdeskAccounts.map(a => a.id)
                    this.availableAccounts = (data.available_accounts || []).filter(
                        a => !helpdeskIds.includes(a.id)
                    )

                    this.$store.commit('endProgress')
                })
                .catch(error => {
                    console.error('Error loading settings:', error)
                    this.$store.commit('endProgress')
                })
                .finally(() => {
                    this.loading = false
                })
        },

        addHelpdeskAccount() {
            if (!this.selectedAccountId) return

            this.addingAccount = true
            this.axios.put(`google/${this.selectedAccountId}/helpdesk-inbox`)
                .then(() => {
                    this.$store.commit('setSnackbar', {
                        value: true,
                        text: 'Account aggiunto come Helpdesk',
                        color: 'success',
                        timeout: 3000
                    })
                    this.showAddAccountDialog = false
                    this.selectedAccountId = null
                    this.loadSettings()
                })
                .catch(error => {
                    console.error('Error adding account:', error)
                    this.$store.commit('setSnackbar', {
                        value: true,
                        text: error.response?.data?.message || 'Errore durante l\'aggiunta',
                        color: 'error',
                        timeout: 5000
                    })
                })
                .finally(() => {
                    this.addingAccount = false
                })
        },

        removeAccount(accountId) {
            this.accountToRemove = accountId
            this.showRemoveDialog = true
        },

        confirmRemoveAccount() {
            if (!this.accountToRemove) return

            this.removingAccount = true
            this.axios.delete(`google/${this.accountToRemove}/helpdesk-inbox`)
                .then(() => {
                    this.$store.commit('setSnackbar', {
                        value: true,
                        text: 'Account rimosso dall\'Helpdesk',
                        color: 'success',
                        timeout: 3000
                    })
                    this.showRemoveDialog = false
                    this.accountToRemove = null
                    this.loadSettings()
                })
                .catch(error => {
                    console.error('Error removing account:', error)
                    this.$store.commit('setSnackbar', {
                        value: true,
                        text: error.response?.data?.message || 'Errore durante la rimozione',
                        color: 'error',
                        timeout: 5000
                    })
                })
                .finally(() => {
                    this.removingAccount = false
                })
        },

        // Toglie code/oauth_error dall'indirizzo senza passare dal router:
        // AppTabs rimonta il contenuto a ogni cambio di fullPath, e un
        // $router.replace azzererebbe le schede riportando sulla prima.
        // Il code e' monouso: deve sparire prima di un eventuale refresh.
        pulisciQueryOAuth() {
            const hash = '#' + this.$route.path
            window.history.replaceState(window.history.state, '', window.location.pathname + window.location.search + hash)
        },

        iconaProvider(provider) {
            return provider === 'microsoft' ? 'mdi-microsoft' : 'mdi-google'
        },

        // origin=helpdesk fa tornare Google su questa pagina (routes/web.php
        // lo legge dallo state OAuth) invece che sui calendari.
        connectGoogleAccount(loginHint = null) {
            const params = { type: 'helpdesk', origin: 'helpdesk', spa_origin: window.location.origin }
            if (loginHint) {
                params.login_hint = loginHint
            }
            this.avviaCollegamento('google/oauth', params)
        },

        connectMicrosoftAccount(loginHint = null) {
            const params = { type: 'helpdesk', spa_origin: window.location.origin }
            if (loginHint) {
                params.login_hint = loginHint
            }
            this.avviaCollegamento('microsoft/oauth', params)
        },

        avviaCollegamento(endpoint, params) {
            this.axios.get(endpoint, { params })
                .then(response => {
                    if (response.data.url) {
                        window.location.href = response.data.url
                        return
                    }
                    this.$store.commit('setSnackbar', { value: true, text: 'Impossibile avviare il collegamento: riprova.', color: 'error', timeout: 6000 })
                })
                .catch(error => {
                    console.error('Error getting OAuth URL:', error)
                    this.$store.commit('setSnackbar', { value: true, text: error.response?.data?.message || 'Impossibile avviare il collegamento', color: 'error', timeout: 6000 })
                })
        },

        reconnectAccount(account) {
            // Rifa' OAuth sullo stesso indirizzo: updateOrCreate aggiorna il token
            // sulla riga esistente, preservando lo storico dei ticket.
            if (account.provider === 'microsoft') {
                this.connectMicrosoftAccount(account.name)
            } else {
                this.connectGoogleAccount(account.name)
            }
        },

        // Ritorno dal consenso OAuth: il code e' monouso, va tolto dall'indirizzo
        // appena consumato. provider=microsoft lo aggiunge la rotta pubblica di
        // ritorno; senza leggerlo il codice Microsoft finirebbe all'endpoint Google.
        consumaCodiceOAuth() {
            const code = this.$route.query.code
            const endpoint = this.$route.query.provider === 'microsoft' ? 'microsoft/oauth' : 'google/oauth'

            this.pulisciQueryOAuth()

            return this.axios.get(endpoint, { params: { code: code, type: 'helpdesk' } })
                .then(() => {
                    this.$store.commit('setSnackbar', { value: true, text: 'Casella collegata', color: 'success', timeout: 4000 })
                })
                .catch(error => {
                    this.$store.commit('setSnackbar', { value: true, text: error.response?.data?.message || 'Collegamento della casella non riuscito', color: 'error', timeout: 6000 })
                })
        },

        loadNotificationSettings() {
            this.axios.get('settings/helpdesk').then(response => {
                _.forEach(response.data.settings, (setting) => {
                    if (this.notificationSettings.hasOwnProperty(setting.key)) {
                        this.$set(this.notificationSettings, setting.key, setting.value)
                    }
                })
            })
        },

        saveNotificationSettings() {
            this.savingNotifications = true
            let data = { model: 'helpdesk' }
            _.forEach(this.notificationSettings, (value, key) => {
                data[key] = value == undefined ? null : value
            })
            this.axios.post('settings/', data).then(response => {
                this.$store.commit('setSnackbar', {
                    timeout: 6000,
                    text: 'Impostazioni salvate',
                    value: true,
                    color: 'success'
                })
                this.$store.commit('SET_SETTINGS', response.data.settings)
            }).catch(error => {
                console.error('Error saving notification settings:', error)
                this.$store.commit('setSnackbar', {
                    value: true,
                    text: 'Errore nel salvataggio',
                    color: 'error',
                    timeout: 5000
                })
            }).finally(() => {
                this.savingNotifications = false
            })
        },

        saveDefaultAccount() {
            this.savingDefaultAccount = true
            this.axios.post('save-helpdesk-settings', {
                default_account_id: this.defaultAccountId
            })
                .then(() => {
                    this.$store.commit('setSnackbar', {
                        value: true,
                        text: 'Account di default salvato',
                        color: 'success',
                        timeout: 3000
                    })
                })
                .catch(error => {
                    console.error('Error saving default account:', error)
                    this.$store.commit('setSnackbar', {
                        value: true,
                        text: 'Errore nel salvataggio',
                        color: 'error',
                        timeout: 5000
                    })
                })
                .finally(() => {
                    this.savingDefaultAccount = false
                })
        }
    },

    mounted() {
        // Il code va scambiato prima di caricare i settings, altrimenti la
        // casella appena collegata non comparirebbe nell'elenco.
        if (this.$route.query.code) {
            this.consumaCodiceOAuth().finally(() => {
                this.loadSettings()
                this.loadNotificationSettings()
            })
            return
        }

        // Consenso negato o annullato dal provider: la rotta pubblica di ritorno
        // ci riporta qui con oauth_error invece di lasciare l'utente su una
        // pagina di errore fuori dall'applicazione.
        if (this.$route.query.oauth_error) {
            const annullato = this.$route.query.oauth_error === 'access_denied'
            this.pulisciQueryOAuth()
            this.$store.commit('setSnackbar', {
                value: true,
                text: annullato
                    ? 'Collegamento annullato o non autorizzato: la casella non è stata collegata.'
                    : 'Il provider ha rifiutato il collegamento: riprova o contatta l\'amministratore.',
                color: 'warning',
                timeout: 8000
            })
        }

        this.loadSettings()
        this.loadNotificationSettings()
    }
}
</script>
